import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { QrCode, Phone, Search, RotateCcw, Check } from "lucide-react";
import { api } from "../api";
import { toast } from "sonner";
import { TicketScanner } from "../components/TicketScanner";

// Pagina staff evento: accesso delegato via token (NESSUN login YouPAM). Mobile-first.
// Espone SOLO nome/cognome/fascia/stato check-in/telefono. Nessun dato sanitario.
export default function StaffEvent() {
  const { token } = useParams();
  const { t } = useTranslation();
  const [session, setSession] = useState(null);
  const [invalid, setInvalid] = useState(false);
  const [parts, setParts] = useState([]);
  const [q, setQ] = useState("");
  const [scan, setScan] = useState(false);

  const loadSession = useCallback(() => api.get(`/event-staff/session?token=${encodeURIComponent(token)}`).then(({ data }) => setSession(data)).catch(() => setInvalid(true)), [token]);
  const loadParts = useCallback(() => api.get(`/event-staff/participants?token=${encodeURIComponent(token)}`).then(({ data }) => setParts(data)).catch(() => {}), [token]);
  useEffect(() => { loadSession(); loadParts(); }, [loadSession, loadParts]);

  const refresh = () => { loadSession(); loadParts(); };
  const checkin = async (rid) => { try { const { data } = await api.post("/event-staff/checkin", { token, registration_id: rid }); if (data.result === "ok") toast.success(t("staff.checked_in")); else if (data.result === "already_used") toast.info(t("scan.res_already_used")); refresh(); } catch { toast.error("Errore"); } };
  const undo = async (rid) => { try { await api.post("/event-staff/checkin/undo", { token, registration_id: rid }); refresh(); } catch { toast.error("Errore"); } };

  if (invalid) return <div className="min-h-screen flex items-center justify-center p-6"><div className="yp-card p-6 text-center text-red-600 font-medium" data-testid="staff-invalid">{t("staff.invalid_link")}</div></div>;
  if (!session) return <div className="min-h-screen flex items-center justify-center text-slate-400" data-testid="staff-loading">{t("staff.loading")}</div>;

  const place = [session.address, session.city, session.country].filter(Boolean).join(", ");
  const firstSlot = (session.slots || [])[0];
  const ql = q.trim().toLowerCase();
  const visible = ql ? parts.filter((p) => `${p.first_name} ${p.last_name} ${p.registration_id}`.toLowerCase().includes(ql)) : parts;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="staff-event-page">
      <div className="sticky top-0 bg-white border-b border-slate-100 z-10 px-4 py-3">
        <h1 className="text-lg font-bold leading-tight" data-testid="staff-event-title">{session.event_title}</h1>
        <div className="text-xs text-slate-500 mt-0.5">{firstSlot ? new Date(firstSlot.start_at).toLocaleString() : ""}{place ? ` · ${place}` : ""}</div>
        <div className="mt-2 inline-flex items-center gap-2 text-sm font-medium bg-slate-100 rounded-full px-3 py-1" data-testid="staff-counts">
          <span>{t("staff.checkins")}: <strong className="text-emerald-600">{session.checked_in}</strong> / {session.registered}</span>
        </div>
      </div>

      <div className="max-w-lg mx-auto p-4 space-y-4">
        <button className="yp-btn yp-btn-primary w-full inline-flex items-center justify-center gap-2 !py-3" onClick={() => setScan(true)} data-testid="staff-scan-btn"><QrCode className="w-5 h-5" />{t("staff.scan_qr")}</button>

        <form onSubmit={(e) => e.preventDefault()} className="flex gap-2">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input className="yp-input w-full !pl-9" placeholder={t("staff.search_ph")} value={q} onChange={(e) => setQ(e.target.value)} data-testid="staff-search-input" />
          </div>
          <button type="submit" className="yp-btn yp-btn-outline shrink-0" data-testid="staff-search-btn">{t("staff.search_participant")}</button>
        </form>

        <div className="space-y-2">
          {visible.length === 0 && <div className="text-slate-400 text-sm text-center py-6" data-testid="staff-no-participants">{t("staff.no_participants")}</div>}
          {visible.map((p) => (
            <div key={p.registration_id} className="yp-card p-4" data-testid="staff-participant">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-semibold truncate">{p.first_name} {p.last_name}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{p.slot_start ? `${t("staff.slot")}: ${new Date(p.slot_start).toLocaleString()}` : ""}</div>
                  <div className="mt-1">
                    {p.checked_in
                      ? <span className="text-xs font-medium text-emerald-600 inline-flex items-center gap-1" data-testid="staff-part-checked"><Check className="w-3.5 h-3.5" />{t("staff.checked_in")}</span>
                      : <span className="text-xs text-amber-600" data-testid="staff-part-unchecked">{t("staff.not_checked")}</span>}
                  </div>
                </div>
                {p.phone && <a href={`tel:${p.phone}`} className="yp-btn yp-btn-outline !py-1.5 !px-3 text-xs shrink-0 inline-flex items-center gap-1" data-testid="staff-call-btn"><Phone className="w-3.5 h-3.5" />{t("staff.call")}</a>}
              </div>
              <div className="mt-3">
                {p.checked_in
                  ? <button className="yp-btn yp-btn-ghost w-full !py-2 text-xs text-slate-600 inline-flex items-center justify-center gap-1" onClick={() => undo(p.registration_id)} data-testid="staff-undo-btn"><RotateCcw className="w-3.5 h-3.5" />{t("staff.undo_checkin")}</button>
                  : <button className="yp-btn yp-btn-primary w-full !py-2 text-xs inline-flex items-center justify-center gap-1" onClick={() => checkin(p.registration_id)} data-testid="staff-checkin-btn"><Check className="w-4 h-4" />{t("staff.confirm_checkin")}</button>}
              </div>
            </div>))}
        </div>
      </div>

      {scan && <TicketScanner event={{ id: session.event_id, title: session.event_title }} staffToken={token} onResult={refresh} onClose={() => { setScan(false); refresh(); }} />}
    </div>);
}
